import { useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select } from '../../components/ui/select';
import { Pagination } from '../../components/ui/Pagination';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, Thead, Tbody, Tr, Th, Td } from '../../components/ui/table';
import { Dialog } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { useUsers, useUpdateUser, useDeleteUser } from '../../hooks/use-users';
import { useToast } from '../../components/ui/toast';
import { formatDate } from '../../lib/utils';

const ROLE_LABELS: Record<string, string> = { CLIENT: 'Client', AGENT: 'Agent', ADMIN: 'Admin' };
const ROLE_COLORS: Record<string, string> = { CLIENT: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', AGENT: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300', ADMIN: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' };

export function UsersPage() {
  const [page, setPage] = useState(1);
  const [role, setRole] = useState('');
  const { toast } = useToast();
  const { data, isLoading } = useUsers({ page, pageSize: 15, role: role || undefined });
  const users = data?.data ?? [];
  const pagination = data?.meta.pagination;

  const [editing, setEditing] = useState<{ id: string; name: string; role: string; isActive: boolean } | null>(null);
  const updateUser = useUpdateUser();
  const deleteUserMutation = useDeleteUser({
    onSuccess: () => toast('success', 'Utilizatorul a fost șters'),
    onError: (e) => toast('error', `Eroare la ștergere: ${e.message}`),
  });

  const handleSave = async () => {
    if (!editing) return;
    try {
      await updateUser.mutateAsync({ id: editing.id, name: editing.name, role: editing.role as 'CLIENT' | 'AGENT' | 'ADMIN', isActive: editing.isActive });
      toast('success', 'Utilizatorul a fost salvat cu succes');
    } catch (e) {
      toast('error', `Eroare la salvare: ${(e as Error).message}`);
      return;
    }
    setEditing(null);
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">Utilizatori</h1>

      <Card className="p-4">
        <Select
          options={[{ value: '', label: 'Toate rolurile' }, { value: 'CLIENT', label: 'Client' }, { value: 'AGENT', label: 'Agent' }, { value: 'ADMIN', label: 'Admin' }]}
          value={role}
          onChange={(e) => { setRole(e.target.value); setPage(1); }}
        />
      </Card>

      <Card>
        <Table>
          <Thead>
            <Tr>
              <Th>Nume</Th>
              <Th>Email</Th>
              <Th>Rol</Th>
              <Th>Status</Th>
              <Th>Ultima logare</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading && Array.from({ length: 5 }).map((_, i) => (
              <Tr key={i}><Td colSpan={6}><Skeleton className="h-4 w-full" /></Td></Tr>
            ))}
            {!isLoading && users.length === 0 && (
              <Tr><Td colSpan={6} className="text-center text-gray-400">Nu s-au găsit utilizatori</Td></Tr>
            )}
            {users.map((u) => (
              <Tr key={u.id}>
                <Td className="font-medium">{u.name}</Td>
                <Td className="text-sm text-gray-500">{u.email}</Td>
                <Td><Badge className={ROLE_COLORS[u.role]}>{ROLE_LABELS[u.role]}</Badge></Td>
                <Td><Badge className={u.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 text-gray-500 dark:bg-dark-border dark:text-dark-muted'}>{u.isActive ? 'Activ' : 'Inactiv'}</Badge></Td>
                <Td className="text-sm text-gray-500">{u.lastLoginAt ? formatDate(u.lastLoginAt) : '—'}</Td>
                <Td>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing({ id: u.id, name: u.name, role: u.role, isActive: u.isActive })}>Editează</Button>
                    {u.role === 'CLIENT' && (
                      <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => {
                        if (window.confirm(`Sigur doriți să ștergeți utilizatorul "${u.name}" (${u.email})? Ticketele create de acest utilizator vor fi păstrate.`)) {
                          deleteUserMutation.mutate(u.id);
                        }
                      }}>Șterge</Button>
                    )}
                  </div>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {pagination && <Pagination page={pagination.page} totalPages={pagination.totalPages} onPageChange={setPage} />}
      </Card>

      {editing && (
        <Dialog open onClose={() => setEditing(null)} title="Editează utilizator">
          <div className="space-y-4">
            <Input label="Nume" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            <Select
              label="Rol"
              options={[{ value: 'CLIENT', label: 'Client' }, { value: 'AGENT', label: 'Agent' }, { value: 'ADMIN', label: 'Admin' }]}
              value={editing.role}
              onChange={(e) => setEditing({ ...editing, role: e.target.value })}
            />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editing.isActive} onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })} className="rounded" />
              Activ
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Anulează</Button>
              <Button onClick={handleSave} disabled={updateUser.isPending}>Salvează</Button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}
